import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_INVENTORY_CATEGORIES = 'FETCH_INVENTORY_CATEGORIES';
export const FETCH_INVENTORY_CATEGORIES_SUCCESS = 'FETCH_INVENTORY_CATEGORIES_SUCCESS';
export const FETCH_INVENTORY_CATEGORIES_FAIL = 'FETCH_INVENTORY_CATEGORIES_FAIL';
export const CLEAR_INVENTORY_CATEGORIES = 'CLEAR_INVENTORY_CATEGORIES';
export const CREATE_INVENTORY_CATEGORY = 'CREATE_INVENTORY_CATEGORY';
export const CREATE_INVENTORY_CATEGORY_SUCCESS = 'CREATE_INVENTORY_CATEGORY_SUCCESS';
export const CREATE_INVENTORY_CATEGORY_FAIL = 'CREATE_INVENTORY_CATEGORY_FAIL';
export const UPDATE_INVENTORY_CATEGORY = 'UPDATE_INVENTORY_CATEGORY';
export const UPDATE_INVENTORY_CATEGORY_SUCCESS = 'UPDATE_INVENTORY_CATEGORY_SUCCESS';
export const UPDATE_INVENTORY_CATEGORY_FAIL = 'UPDATE_INVENTORY_CATEGORY_FAIL';
export const DELETE_INVENTORY_CATEGORY = 'DELETE_INVENTORY_CATEGORY';
export const DELETE_INVENTORY_CATEGORY_SUCCESS = 'DELETE_INVENTORY_CATEGORY_SUCCESS';
export const DELETE_INVENTORY_CATEGORY_FAIL = 'DELETE_INVENTORY_CATEGORY_FAIL';

// Reducer
export default function inventoryCategoriesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_INVENTORY_CATEGORIES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INVENTORY_CATEGORIES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORY_CATEGORIES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_INVENTORY_CATEGORIES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_INVENTORY_CATEGORY_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_INVENTORY_CATEGORY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_INVENTORY_CATEGORY_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchInventoryCategories(payload) {
  return { type: FETCH_INVENTORY_CATEGORIES, payload };
}

export function fetchInventoryCategoriesSuccess(payload) {
  return { type: FETCH_INVENTORY_CATEGORIES_SUCCESS, payload };
}

export function fetchInventoryCategoriesFail(payload) {
  return { type: FETCH_INVENTORY_CATEGORIES_FAIL, payload };
}

export function clearInventoryCategories(payload) {
  return { type: CLEAR_INVENTORY_CATEGORIES, payload };
}

export function createInventoryCategory(payload) {
  return { type: CREATE_INVENTORY_CATEGORY, payload };
}

export function createInventoryCategorySuccess(payload) {
  return { type: CREATE_INVENTORY_CATEGORY_SUCCESS, payload };
}

export function createInventoryCategoryFail(payload) {
  return { type: CREATE_INVENTORY_CATEGORY_FAIL, payload };
}

export function updateInventoryCategory(payload) {
  return { type: UPDATE_INVENTORY_CATEGORY, payload };
}

export function updateInventoryCategorySuccess(payload) {
  return { type: UPDATE_INVENTORY_CATEGORY_SUCCESS, payload };
}

export function updateInventoryCategoryFail(payload) {
  return { type: UPDATE_INVENTORY_CATEGORY_FAIL, payload };
}

export function deleteInventoryCategory(payload) {
  return { type: DELETE_INVENTORY_CATEGORY, payload };
}

export function deleteInventoryCategorySuccess(payload) {
  return { type: DELETE_INVENTORY_CATEGORY_SUCCESS, payload };
}

export function deleteInventoryCategoryFail(payload) {
  return { type: DELETE_INVENTORY_CATEGORY_FAIL, payload };
}

// Requests

let abort;

export function requestFetchInventoryCategories(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`inventory/category/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('inventory/category/');
  abort = request.abort;
  return request.res;
}

export function requestSearchInventoryCategories({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('inventory/category/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateInventoryCategory(payload) {
  return callApi('inventory/category/', 'POST', payload);
}

export function requestUpdateInventoryCategory(payload) {
  return callApi('inventory/category/', 'PATCH', payload);
}

export function requestDeleteInventoryCategory(payload) {
  return callApi(`inventory/category/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchInventoryCategoriesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchInventoryCategories, action.payload)
      : yield call(requestFetchInventoryCategories, action.payload);
    yield put(fetchInventoryCategoriesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchInventoryCategoriesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createInventoryCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateInventoryCategory, values);
    yield put(createInventoryCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Inventory Category successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createInventoryCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInventoryCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateInventoryCategory, values);
    yield put(updateInventoryCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Category successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateInventoryCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInventoryCategorySaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteInventoryCategory, ID);
    yield put(deleteInventoryCategorySuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Category successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteInventoryCategoryFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Inventory Category',
      variant: 'warning',
    }));
  }
}

export function* inventoryCategoriesSaga() {
  yield takeLatest(FETCH_INVENTORY_CATEGORIES, fetchInventoryCategoriesSaga);
  yield takeEvery(CREATE_INVENTORY_CATEGORY, createInventoryCategorySaga);
  yield takeEvery(UPDATE_INVENTORY_CATEGORY, updateInventoryCategorySaga);
  yield takeEvery(DELETE_INVENTORY_CATEGORY, deleteInventoryCategorySaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/inventoryCategories.js